/**
 * @nanokart/tryon-studio-sdk
 * useCart hook - Manage shopping cart
 */

import { useCallback, useMemo } from 'react';
import { useStore } from './useStore';
import type { CatalogItem, CartItem, TryOnResult } from '../../types/config';

export interface UseCartReturn {
  /** Items that have been tried on (soft cart) */
  softCart: CartItem[];
  /** Items marked for purchase (hard cart) */
  hardCart: CartItem[];
  /** Whether cart drawer is open */
  isOpen: boolean;
  /** Total items in hard cart */
  totalItems: number;
  /** Total price of hard cart */
  totalPrice: number;
  /** Add to soft cart (after try-on) */
  addToSoftCart: (item: CatalogItem, tryonResult?: TryOnResult) => void;
  /** Add to hard cart (for purchase) */
  addToHardCart: (item: CatalogItem, size?: string) => void;
  /** Remove from cart */
  removeFromCart: (productId: string, cartType: 'soft' | 'hard') => void;
  /** Toggle cart drawer */
  toggleCart: () => void;
  /** Clear entire cart */
  clearCart: () => void;
  /** Check if item is in soft cart */
  isInSoftCart: (productId: string) => boolean;
  /** Check if item is in hard cart */
  isInHardCart: (productId: string) => boolean;
  /** Get soft cart item */
  getSoftCartItem: (productId: string) => CartItem | undefined;
  /** Whether partner has Add to Cart webhook configured */
  hasAddToCartWebhook: boolean;
  /** Whether partner config is still loading */
  isPartnerConfigLoading: boolean;
  /** Invoke the Add to Cart webhook (for partners with webhook configured) */
  invokeAddToCartWebhook: (item: CatalogItem, tryonResult?: TryOnResult, size?: string) => Promise<boolean>;
  /** Record purchase intent for ROI analytics (always called on Add to Cart) */
  recordPurchaseIntent: (item: CatalogItem, tryonResult?: TryOnResult, size?: string) => Promise<void>;
}

/**
 * Hook for managing shopping cart
 * 
 * @example
 * ```tsx
 * const { hardCart, addToHardCart, totalPrice, toggleCart } = useCart();
 * 
 * return (
 *   <div>
 *     <Button onClick={toggleCart}>
 *       Cart ({hardCart.length})
 *     </Button>
 *     <CartDrawer items={hardCart} total={totalPrice} />
 *   </div>
 * );
 * ```
 */
export function useCart(): UseCartReturn {
  const softCart = useStore((s) => s.cart.softCart);
  const hardCart = useStore((s) => s.cart.hardCart);
  const isOpen = useStore((s) => s.cart.isOpen);
  const hasAddToCartWebhook = useStore((s) => s.partnerConfig.hasAddToCartWebhook);
  const isPartnerConfigLoading = useStore((s) => s.partnerConfig.isLoading);
  
  const addToSoftCartAction = useStore((s) => s.addToSoftCart);
  const addToHardCartAction = useStore((s) => s.addToHardCart);
  const removeFromCartAction = useStore((s) => s.removeFromCart);
  const toggleCartDrawer = useStore((s) => s.toggleCartDrawer);
  const clearCartAction = useStore((s) => s.clearCart);
  const invokeAddToCartWebhookAction = useStore((s) => s.invokeAddToCartWebhook);
  const recordPurchaseIntentAction = useStore((s) => s.recordPurchaseIntent);

  const totalItems = useMemo(() => {
    return hardCart.reduce((sum, item) => sum + item.quantity, 0);
  }, [hardCart]);

  const totalPrice = useMemo(() => {
    return hardCart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  }, [hardCart]);

  const addToSoftCart = useCallback(
    (item: CatalogItem, tryonResult?: TryOnResult) => {
      addToSoftCartAction(item, tryonResult);
    },
    [addToSoftCartAction]
  );

  const addToHardCart = useCallback(
    (item: CatalogItem, size?: string) => {
      addToHardCartAction(item, size);
    },
    [addToHardCartAction]
  );

  const removeFromCart = useCallback(
    (productId: string, cartType: 'soft' | 'hard') => {
      removeFromCartAction(productId, cartType);
    },
    [removeFromCartAction]
  );

  const toggleCart = useCallback(() => {
    toggleCartDrawer();
  }, [toggleCartDrawer]);

  const clearCart = useCallback(() => {
    clearCartAction();
  }, [clearCartAction]);

  const isInSoftCart = useCallback(
    (productId: string) => {
      return softCart.some((item) => item.productId === productId);
    },
    [softCart]
  );

  const isInHardCart = useCallback(
    (productId: string) => {
      return hardCart.some((item) => item.productId === productId);
    },
    [hardCart]
  );

  const getSoftCartItem = useCallback(
    (productId: string) => {
      return softCart.find((item) => item.productId === productId);
    },
    [softCart]
  );

  return {
    softCart,
    hardCart,
    isOpen,
    totalItems,
    totalPrice,
    addToSoftCart,
    addToHardCart,
    removeFromCart,
    toggleCart,
    clearCart,
    isInSoftCart,
    isInHardCart,
    getSoftCartItem,
    hasAddToCartWebhook,
    isPartnerConfigLoading,
    invokeAddToCartWebhook: invokeAddToCartWebhookAction,
    recordPurchaseIntent: recordPurchaseIntentAction,
  };
}
